import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import BerryVideo from '../assets/video/BerryBloomer.mp4';
import {
  FaChartLine,
  FaGlobe,
  FaBullhorn,
  FaUsers,
  FaHandshake,
  FaLaptopCode,
  FaMobileAlt,
  FaBullseye,
  FaChartPie,
  FaRocket
} from 'react-icons/fa';

const BRAND_GRADIENT =
  "linear-gradient(135deg, #0047ff, #00c4ff)";

const TEXT_LIGHT = "#cbd5e1";

const API_BASE = 'https://berrybloomer.com/api';

/**
 * Builds an ordered list of candidate URLs for a client logo, mirroring
 * the fix applied in Client.jsx (AdminClients page). The backend's image
 * route is still unconfirmed — filePath (e.g. "client_logos/6.png") is
 * the actual saved path and should work once the backend route is
 * confirmed, but until then we try several shapes in order and fall back
 * to a text badge only if all of them fail.
 */
const buildLogoCandidates = (client) => {
  const filePath = client?.filePath; // e.g. "client_logos/6.png"
  const fileName = client?.fileName ?? client?.imageName ?? client?.filename ?? null; // e.g. "6.png"
  const legacy = client?.logoUrl ?? client?.image ?? client?.logo ?? null;
  const id = client?.id;

  const encodeSegments = (path) =>
    path.replace(/^\/+/, '').split('/').map(encodeURIComponent).join('/');

  const candidates = [];
  if (legacy) {
    candidates.push(
      legacy.startsWith('http://') || legacy.startsWith('https://')
        ? legacy
        : `${API_BASE}/client-logo/image/${encodeSegments(legacy)}`,
    );
  }
  if (filePath) candidates.push(`${API_BASE}/client-logo/image/${encodeSegments(filePath)}`);
  if (fileName && fileName !== filePath) {
    candidates.push(`${API_BASE}/client-logo/image/${encodeSegments(fileName)}`);
  }
  if (id != null) candidates.push(`${API_BASE}/client-logo/${id}/image`);
  if (filePath) candidates.push(`${API_BASE}/${encodeSegments(filePath)}`);
  if (filePath) candidates.push(`${API_BASE}/images/${encodeSegments(filePath)}`);
  if (filePath) candidates.push(`https://berrybloomer.com/images/${encodeSegments(filePath)}`);
  if (filePath) candidates.push(`https://berrybloomer.com/${encodeSegments(filePath)}`);

  return candidates;
};

/* ================= COLORS ================= */
const TimelineWrapper = styled.div`
  max-width:1000px;
  margin:0 auto;
  position:relative;
  padding-left:40px;

  &::before{
    content:'';
    position:absolute;
    left:18px;
    top:0;
    width:3px;
    height:100%;
    background:
      linear-gradient(
        180deg,
        #00c4ff,
        #0047ff,
        #7c3aed
      );
    border-radius:20px;
  }
`;

const TimelineItem = styled(motion.div)`
  position:relative;
  margin-bottom:45px;
  padding:28px 30px;
  border-radius:24px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  backdrop-filter:blur(12px);
  transition:0.4s ease;

  &:hover{
    transform: translateX(10px);
    border: 1px solid rgba(0,196,255,0.25);
    box-shadow: 0 20px 60px rgba(0,71,255,0.2);
  }
`;

const TimelineDot = styled.div`
  position:absolute;
  left:-34px;
  top:35px;
  width:22px;
  height:22px;
  border-radius:50%;
  background:${BRAND_GRADIENT};
  box-shadow: 0 0 20px rgba(0,196,255,0.5);
`;

const TimelineStep = styled.div`
  font-size:0.85rem;
  letter-spacing:3px;
  color:#00c4ff;
  margin-bottom:12px;
  font-weight:600;
`;

const TimelineTitle = styled.h3`
  font-size:1.6rem;
  margin-bottom:14px;
  color:white;
`;

const TimelineText = styled.p`
  color:${TEXT_LIGHT};
  line-height:1.9;
  font-size:1rem;
`;

const JourneySection = styled.section`
  width:100%;
  padding:70px 20px;
  position:relative;
  overflow:hidden;
`;

const JourneyContainer = styled.div`
  max-width:1300px;
  margin:auto;
`;

const JourneyTitle = styled.h2`
  text-align:center;
  font-size: clamp(2.2rem,5vw,4rem);
  margin-bottom:18px;
  background:${BRAND_GRADIENT};
  -webkit-background-clip:text;
  -webkit-text-fill-color:transparent;
`;

const JourneySub = styled.p`
  text-align:center;
  max-width:900px;
  margin:0 auto 70px;
  color:${TEXT_LIGHT};
  line-height:1.9;
  font-size:1.08rem;
`;

const JourneyGrid = styled.div`
  display:grid;
  grid-template-columns: repeat(5,1fr);
  gap:22px;
  align-items:stretch;

  @media(max-width:1200px){
    grid-template-columns: repeat(3,1fr);
  }

  @media(max-width:900px){
    grid-template-columns: repeat(2,1fr);
  }

  @media(max-width:600px){
    grid-template-columns: 1fr;
  }
`;

const JourneyCard = styled(motion.div)`
  position:relative;
  padding:35px;
  border-radius:28px;
  overflow:visible;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  backdrop-filter:blur(12px);
  transition:0.4s ease;
  cursor:pointer;

  &:hover{
    transform: translateY(-12px) scale(1.03);
    border: 1px solid rgba(0,196,255,0.25);
    box-shadow: 0 20px 60px rgba(0,71,255,0.25);
  }

  &:not(:last-child)::after{
    content:'➜';
    position:absolute;
    right:-22px;
    top:50%;
    transform:translateY(-50%);
    font-size:2rem;
    color:#00c4ff;
    animation: arrowFlow 1.5s infinite ease-in-out;
  }

  @keyframes arrowFlow{
    0%{
      transform: translateY(-50%) translateX(0px);
      opacity:0.5;
    }
    50%{
      transform: translateY(-50%) translateX(6px);
      opacity:1;
    }
    100%{
      transform: translateY(-50%) translateX(0px);
      opacity:0.5;
    }
  }

  @media(max-width:900px){
    &:not(:last-child)::after{
      content:'↓';
      right:auto;
      left:50%;
      bottom:-35px;
      top:auto;
      transform:translateX(-50%);
    }
  }
`;

const StepNo = styled.div`
  position:absolute;
  top:20px;
  right:22px;
  width:60px;
  height:60px;
  border-radius:20px;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:1.3rem;
  font-weight:900;
  color:white;
  background:${BRAND_GRADIENT};
  box-shadow: 0 10px 30px rgba(0,196,255,0.35);
  border: 1px solid rgba(255,255,255,0.12);
  z-index:2;
`;

const JourneyIcon = styled.div`
  width:75px;
  height:75px;
  border-radius:22px;
  display:flex;
  align-items:center;
  justify-content:center;
  margin-bottom:25px;
  font-size:2rem;
  background:${BRAND_GRADIENT};
  box-shadow: 0 10px 30px rgba(0,71,255,0.35);
`;

const JourneyCardTitle = styled.h3`
  font-size:1.4rem;
  margin-bottom:18px;
  color:white;
`;

const JourneyDesc = styled.p`
  color:${TEXT_LIGHT};
  line-height:1.9;
  font-size:0.98rem;
`;

/* ================= MAIN WRAPPER ================= */

const Wrapper = styled.div`
  font-family: 'Poppins', sans-serif;
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  position: relative;
  color: white;

  background:
    radial-gradient(circle at top right, rgba(0,196,255,0.18), transparent 25%),
    radial-gradient(circle at bottom left, rgba(0,71,255,0.18), transparent 25%),
    #050816;
`;

/* ================= LOADER ================= */

const LoaderWrapper = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 99999;
  overflow: hidden;
  padding: 20px;
  box-sizing: border-box;

  background:
    radial-gradient(circle at top right, rgba(0,196,255,0.18), transparent 25%),
    radial-gradient(circle at bottom left, rgba(0,71,255,0.18), transparent 25%),
    #050816;
`;

const GlowCircle = styled.div`
  position: absolute;
  width: min(420px, 80vw);
  height: min(420px, 80vw);
  border-radius: 50%;
  background: rgba(0,196,255,0.16);
  filter: blur(120px);
  animation: pulseGlow 2s infinite ease-in-out;

  @keyframes pulseGlow {
    0% { transform: scale(0.9); opacity: 0.6; }
    50% { transform: scale(1.05); opacity: 1; }
    100% { transform: scale(0.9); opacity: 0.6; }
  }
`;

const LoaderContent = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(12px, 3vh, 18px);
  z-index: 2;
  width: 100%;
  max-width: 100%;
  padding: 0 16px;
  box-sizing: border-box;
  text-align: center;
`;

const LogoCircle = styled.div`
  width: clamp(80px, 20vw, 115px);
  height: clamp(80px, 20vw, 115px);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;

  background:
    linear-gradient(
      135deg,
      rgba(124,58,237,0.22),
      rgba(37,99,235,0.22),
      rgba(56,189,248,0.18)
    );

  border: 1px solid rgba(255,255,255,0.08);
  backdrop-filter: blur(12px);
  box-shadow: 0 0 50px rgba(37,99,235,0.28);
  position: relative;
  overflow: hidden;

  &::before{
    content:'';
    position:absolute;
    inset:-30%;
    background:
      conic-gradient(
        from 180deg,
        transparent,
        rgba(255,255,255,0.22),
        transparent
      );
    animation: shineRotate 2.5s linear infinite;
  }

  @keyframes shineRotate{
    from{ transform:rotate(0deg); }
    to{ transform:rotate(360deg); }
  }
`;

const LogoText = styled.div`
  font-size: clamp(2.4rem, 6vw, 3.8rem);
  font-weight: 800;
  line-height: 1;

  background:
    linear-gradient(
      135deg,
      #7c3aed 0%,
      #6d28d9 18%,
      #4f46e5 38%,
      #2563eb 68%,
      #38bdf8 100%
    );

  background-size: 220% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 22px rgba(79,70,229,0.28);
  animation: logoGradientFlow 4s linear infinite;

  @keyframes logoGradientFlow{
    to{ background-position: 220% center; }
  }
`;

const CompanyName = styled.h1`
  font-size: clamp(1.3rem, 6vw, 4.5rem);
  font-weight: 800;
  letter-spacing: clamp(3px, 1.2vw, 10px);
  text-align: center;
  max-width: 100%;
  margin: 0;

  background: linear-gradient(135deg, #ffffff, #00c4ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const LoadingBar = styled.div`
  width: min(240px, 70vw);
  height: 4px;
  background: rgba(255,255,255,0.08);
  border-radius: 30px;
  overflow: hidden;
`;

const LoadingProgress = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #0047ff, #00c4ff);
  animation: loadingAnim 2.8s ease;

  @keyframes loadingAnim {
    from { width: 0%; }
    to { width: 100%; }
  }
`;

const LoaderText = styled.p`
  color: #cbd5e1;
  letter-spacing: clamp(1.5px, 0.6vw, 3px);
  font-size: clamp(0.7rem, 2.2vw, 0.9rem);
  text-align: center;
  max-width: 90vw;
  margin: 0;
`;

/* ================= HERO ================= */

const Hero = styled.section`
  min-height:auto;
  display:flex;
  flex-direction:column;
  justify-content:flex-start;
  align-items:center;
  text-align:center;
  padding:70px 20px 35px;
`;

const HeroTitle = styled(motion.h1)`
  max-width: 950px;
  font-size: clamp(2.8rem, 7vw, 5.5rem);
  line-height: 1.08;
  font-weight: 800;
  margin-bottom: 24px;
  color: white;
`;

const GradientText = styled.span`
  background: linear-gradient(135deg, #00c4ff, #ffffff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const HeroDesc = styled(motion.p)`
  max-width: 760px;
  font-size: 1.15rem;
  line-height: 1.9;
  color: ${TEXT_LIGHT};
  margin-bottom: 40px;
`;

/* ================= SERVICES ================= */

const SectionHeader = styled.h2`
  text-align: center;
  font-size: clamp(2rem, 5vw, 3rem);
  margin-top: 20px;
  margin-bottom: 10px;

  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Services = styled.section`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
  padding: 35px 20px 55px;
`;

const Card = styled(motion.div)`
  width: 320px;
  padding: 32px 28px;
  border-radius: 24px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  backdrop-filter: blur(10px);
  cursor: pointer;
  transition: 0.35s ease;

  &:hover {
    transform: translateY(-10px);
    border: 1px solid rgba(0,196,255,0.25);
    box-shadow: 0 20px 60px rgba(0,71,255,0.25);
  }
`;

const CardTitle = styled.h3`
  font-size: 1.35rem;
  margin-bottom: 16px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const CardDesc = styled.p`
  color: ${TEXT_LIGHT};
  line-height: 1.8;
`;

/* ================= SINGLE-TRACK MARQUEE ================= */

/* Outer viewport: clips the track and fades edges.
   NO hover-pause — marquee keeps moving. Individual cards react on hover. */
const ClientMarquee = styled.div`
  position: relative;
  width: 100%;
  padding: 40px 0 60px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 6%,
    black 94%,
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 6%,
    black 94%,
    transparent 100%
  );
  user-select: none;
  -webkit-user-select: none;

  @media (max-width: 768px) {
    padding: 28px 0 40px;
  }
`;

/* Single track scrolls continuously left. */
const ClientTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 28px;
  width: max-content;
  will-change: transform;
  animation: ${({ $reverse }) => ($reverse ? 'marqueeRight' : 'marqueeLeft')}
    ${({ $duration }) => $duration}s linear infinite;

  @keyframes marqueeLeft {
    0%   { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }

  @keyframes marqueeRight {
    0%   { transform: translateX(-50%); }
    100% { transform: translateX(0); }
  }

  @media (max-width: 768px) {
    gap: 20px;
  }
`;

/* Larger square logo card — prominent single-row display.
   Logos show in full color and are clearly recognizable.
   Hover = lift + glow + subtle scale, but marquee NEVER pauses. */
const ClientLogo = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.3s ease;
  overflow: hidden;
  flex-shrink: 0;
  padding: 24px;
  user-select: none;
  -webkit-user-select: none;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    transition: all 0.3s ease;
    user-select: none;
    -webkit-user-select: none;
    -webkit-user-drag: none;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-10px) scale(1.05);
    z-index: 10;
    border-color: rgba(0, 196, 255, 0.5);
    background: rgba(255, 255, 255, 0.08);
    box-shadow:
      0 24px 64px rgba(0, 71, 255, 0.45),
      0 0 0 1px rgba(0, 196, 255, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
  }

  @media (max-width: 768px) {
    width: 150px;
    height: 150px;
    padding: 18px;
  }
`;

/* Fallback: branded initial + name shown when the image 404s. */
const LogoFallback = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 100%;
  padding: 0 4px;
  user-select: none;
`;

const LogoFallbackBadge = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  font-weight: 700;
  color: white;
  background: ${BRAND_GRADIENT};
  flex-shrink: 0;
`;

const LogoFallbackName = styled.span`
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 0.2px;
  text-align: left;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NoClientsMessage = styled.p`
  color:${TEXT_LIGHT};
  font-size:1rem;
  text-align:center;
  width:100%;
  padding:20px 0;
`;

/* ================= VIDEO SECTION ================= */

const WhyChooseSection = styled.section`
  width: 100%;
  padding: 30px 10px 50px;
`;

const WhyContainer = styled.div`
  max-width:1200px;
  margin:0 auto;
  display:flex;
  align-items:center;
  gap:28px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius:24px;
  padding:24px;
  backdrop-filter:blur(10px);

  @media(max-width:900px){
    flex-direction:column;
  }
`;

const WhyText = styled.div`
  flex: 1;
`;

const WhyTitle = styled.h3`
  font-size: 2.2rem;
  margin-bottom: 20px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const WhyDescription = styled.p`
  color: ${TEXT_LIGHT};
  line-height: 1.9;
`;

const WhyVideoBox = styled.div`
  flex: 1;
  display:flex;
  justify-content:center;
  align-items:center;
  padding-right:20px;
`;

const StyledVideo = styled.video`
  width: auto;
  height: auto;
  max-width: 110%;
  max-height: 500px;
  object-fit: contain;
  object-position: center;
  border-radius: 20px;
  background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.08);
  box-shadow: 0 10px 30px rgba(0,0,0,0.28);

  @media(max-width:900px){
    max-width:100%;
    max-height:350px;
  }
`;

/* ================= CLIENT LOGO ITEM ================= */

/**
 * Renders a single client logo image, trying each candidate URL in
 * sequence on failure before falling back to a branded initial badge.
 * Mirrors the multi-candidate approach used in Client.jsx (AdminClients
 * page) since the backend's actual image-serving route is still
 * unconfirmed.
 */
function ClientLogoItem({ client, index }) {
  const candidates = React.useRef(buildLogoCandidates(client)).current;
  const [attemptIndex, setAttemptIndex] = useState(0);
  const [broken, setBroken] = useState(candidates.length === 0);

  const src = candidates[attemptIndex];
  const name = client.name || 'Client';
  const initial = name.trim().charAt(0).toUpperCase() || '?';

  if (broken || !src) {
    return (
      <LogoFallback>
        <LogoFallbackBadge>{initial}</LogoFallbackBadge>
        <LogoFallbackName>{name}</LogoFallbackName>
      </LogoFallback>
    );
  }

  return (
    <img
      src={src}
      alt={client.name ? `${client.name} logo` : 'client-logo'}
      draggable={false}
      onError={() => {
        const nextIndex = attemptIndex + 1;
        if (nextIndex < candidates.length) {
          console.warn(
            `[Home] client logo "${src}" failed, trying next candidate:`,
            candidates[nextIndex],
          );
          setAttemptIndex(nextIndex);
          return;
        }
        console.warn('[Home] all candidate logo URLs failed:', { candidates, client });
        setBroken(true);
      }}
    />
  );
}

/* ================= MAIN COMPONENT ================= */

export default function Home() {

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const [clients, setClients] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2800);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  // Fetch client logos directly from the backend server.
  useEffect(() => {
    fetch(`${API_BASE}/client-logo`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to fetch client logos: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data?.[0]) console.log('[Home] sample client record:', data[0]);
        setClients(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error('Error fetching client logos:', err);
        setClients([]);
      });
  }, []);

  if (loading) {
    return (
      <LoaderWrapper>
        <GlowCircle />

        <LoaderContent
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{
              repeat: Infinity,
              duration: 2.8,
              ease: "linear"
            }}
          >
            <LogoCircle>
              <LogoText>B</LogoText>
            </LogoCircle>
          </motion.div>

          <CompanyName>BERRYBLOOMER</CompanyName>

          <LoadingBar>
            <LoadingProgress />
          </LoadingBar>

          <LoaderText>BUILDING DIGITAL EXCELLENCE</LoaderText>
        </LoaderContent>
      </LoaderWrapper>
    );
  }

  const serviceCards = [
    {
      title: 'Mobile App Development',
      desc: 'Modern Android & iOS applications with scalable architecture.',
      path: '/android',
    },
    {
      title: 'Web Development',
      desc: 'Enterprise-grade web platforms with modern technologies.',
      path: '/webapp',
    },
    {
      title: 'UI/UX Design',
      desc: 'Elegant interfaces focused on usability and engagement.',
      path: '/uiux',
    },
  ];

  // Single-track marquee: all clients in one continuous row.
  // Card width (200) + gap (28) = 228px per logo slot.
  const CLIENT_LOGO_WIDTH = 200 + 28;
  const viewportWidth =
    typeof window !== 'undefined' ? window.innerWidth : 1920;

  // Use all clients in a single row
  const repeats =
    clients.length > 0
      ? Math.max(3, Math.ceil((viewportWidth + 800) / (clients.length * CLIENT_LOGO_WIDTH)))
      : 1;
  const half = Array.from({ length: repeats }, () => clients).flat();
  const duration = Math.max(30, half.length * 2.5);
  const marqueeClients = clients.length > 0 ? [...half, ...half] : [];

  return (
    <Wrapper>

      <Hero>
        <HeroTitle
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          Our<GradientText> Services</GradientText>
        </HeroTitle>

        <HeroDesc
          style={{
            maxWidth: "900px",
            marginBottom: "50px"
          }}
        >
          Explore our digital solutions designed
          to help businesses build, grow,
          and scale effectively.
        </HeroDesc>
      </Hero>

      <Services>
        {serviceCards.map(({ title, desc, path }) => (
          <Card
            key={title}
            whileHover={{ y: -8 }}
            onClick={() => navigate(path)}
          >
            <CardTitle>{title}</CardTitle>
            <CardDesc>{desc}</CardDesc>
          </Card>
        ))}
      </Services>

      <SectionHeader style={{ marginTop: "50px", marginBottom: "4px" }}>
        Our Clients
      </SectionHeader>
      <p style={{
        textAlign: 'center',
        color: 'rgba(203, 213, 225, 0.65)',
        fontSize: '0.95rem',
        marginBottom: '8px',
        letterSpacing: '0.2px',
      }}>
        Trusted by teams building the next generation of products
      </p>

      <ClientMarquee>
        {clients.length === 0 ? (
          <NoClientsMessage>
            No client logos to display right now.
          </NoClientsMessage>
        ) : (
          <ClientTrack className="client-track" $duration={duration} $reverse={false}>
            {marqueeClients.map((client, index) => (
              <ClientLogo
                key={`client-${client.id || client.fileName || client.name || index}-${index}`}
                aria-hidden={index >= half.length}
              >
                <ClientLogoItem client={client} index={index} />
              </ClientLogo>
            ))}
          </ClientTrack>
        )}
      </ClientMarquee>

      <SectionHeader
        style={{
          marginTop: "10px",
          marginBottom: "8px"
        }}
      >
        Why Choose Us
      </SectionHeader>

      <WhyChooseSection>
        <WhyContainer>
          <WhyText>
            <WhyTitle>
              Innovation. Performance. Reliability.
            </WhyTitle>

            <WhyDescription>
              Berrybloomer combines cutting-edge
              technology with strategic thinking
              to create scalable, secure, and
              future-ready digital products for
              startups and enterprises.
            </WhyDescription>
          </WhyText>

          <WhyVideoBox>
            <StyledVideo
              src={BerryVideo}
              controls
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
            />
          </WhyVideoBox>
        </WhyContainer>
      </WhyChooseSection>

      <JourneySection>
        <JourneyContainer>
          <JourneyTitle>
            Grow Your Business in Just 5 Steps
          </JourneyTitle>

          <JourneySub>
            Berrybloomer helps businesses build
            a powerful online presence using
            websites, mobile apps, digital marketing,
            branding, automation, and lead generation systems.
          </JourneySub>

          <JourneyGrid>
            <JourneyCard whileHover={{ y: -10 }}>
              <StepNo>01</StepNo>
              <JourneyIcon><FaLaptopCode /></JourneyIcon>
              <JourneyCardTitle>Build Professional Website</JourneyCardTitle>
              <JourneyDesc>
                A premium website builds trust,
                showcases your services,
                explains your business professionally,
                and helps customers understand
                your company easily.
              </JourneyDesc>
            </JourneyCard>

            <JourneyCard whileHover={{ y: -10 }}>
              <StepNo>02</StepNo>
              <JourneyIcon><FaMobileAlt /></JourneyIcon>
              <JourneyCardTitle>Launch Mobile Applications</JourneyCardTitle>
              <JourneyDesc>
                Mobile apps improve customer engagement,
                increase repeat users,
                simplify communication,
                and help businesses stay connected
                with users anytime.
              </JourneyDesc>
            </JourneyCard>

            <JourneyCard whileHover={{ y: -10 }}>
              <StepNo>03</StepNo>
              <JourneyIcon><FaBullseye /></JourneyIcon>
              <JourneyCardTitle>Smart Digital Marketing</JourneyCardTitle>
              <JourneyDesc>
                We run targeted social media campaigns,
                advertisements, and branding strategies
                to reach people genuinely interested
                in your services.
              </JourneyDesc>
            </JourneyCard>

            <JourneyCard whileHover={{ y: -10 }}>
              <StepNo>04</StepNo>
              <JourneyIcon><FaChartPie /></JourneyIcon>
              <JourneyCardTitle>Generate Leads & Analytics</JourneyCardTitle>
              <JourneyDesc>
                Using automation, APIs, and analytics,
                we help businesses collect leads,
                understand customer behavior,
                and improve conversions.
              </JourneyDesc>
            </JourneyCard>

            <JourneyCard whileHover={{ y: -10 }}>
              <StepNo>05</StepNo>
              <JourneyIcon><FaRocket /></JourneyIcon>
              <JourneyCardTitle>Scale Your Business</JourneyCardTitle>
              <JourneyDesc>
                Once your online ecosystem is ready,
                your business becomes scalable,
                globally reachable,
                and more profitable through
                continuous digital growth.
              </JourneyDesc>
            </JourneyCard>
          </JourneyGrid>
        </JourneyContainer>
      </JourneySection>
    </Wrapper>
  );
}